import { PlaneLanding, PlaneTakeoff } from "lucide-react";
import moment from "moment";
import React, { useEffect, useRef } from "react";
import Calendar, { DateUtils, DayModifiers } from "react-day-picker";
import MomentLocaleUtils from "react-day-picker/moment";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { countriesWithUSFormat, monthYear } from "shared/lib/datetime";
import { urlDateToRawDate } from "shared/lib/functions";

import useMediaQuery from "../../hooks/useMediaQuery";

type SelectedDateLabel = {
  date: any;
  label: string;
  variant?: "roundTrip";
};

type Props = {
  availableDates: Date[];
  highlightedDates?: Date[];
  focusableDates?: Date[];
  fromMonth?: string;
  startingDate: string;
  toMonth?: string;
  onDayClick: (day: Date, modifiers?: DayModifiers) => void;
  selectedDate: any;
  selectedDateLabels?: SelectedDateLabel[];
  rangeDates?: {
    from?: Date;
    to?: Date;
  };
  locale: string;
  onCalendarChange: (month: Date) => void;
  onDayMouseEnter?: (day: Date) => void;
  id: string;
  className?: string;
  numberOfMonths?: number;
  mobileNumberOfMonths?: number;
  renderDayFooter?: (day: Date) => React.ReactNode;
  selectedLabel?: string;
  showHighlightedDateBorders?: boolean;
  fixedWeeks?: boolean;
};

function TripDateCalendar(props: Props): React.ReactElement | null {
  const isDesktop = useMediaQuery(1010);
  const defaultCountry = useSelector(
    (state: AppState) =>
      state.storage.frontendSettings?.dealerFrontendSettings?.defaultCountry ??
      ""
  );
  const calendarContainerRef = useRef<HTMLDivElement | null>(null);

  const selectedDateLabels: SelectedDateLabel[] =
    props.selectedDateLabels ||
    (props.selectedDate && props.selectedLabel
      ? [{ date: props.selectedDate, label: props.selectedLabel }]
      : []);
  const highlightedDates = props.highlightedDates || props.availableDates;
  const focusableDates = props.focusableDates || props.availableDates;
  const selectedDays = selectedDateLabels.length
    ? selectedDateLabels.map(({ date }) => date)
    : props.selectedDate;
  const modifiers = {
    availableDates: props.availableDates,
    highlightedDates,
    keyboardFocusable: focusableDates,
    tripRange: (day: Date) => {
      const { from, to } = props.rangeDates || {};

      if (!(from && to)) return false;

      const dayTime = moment(day).startOf("day").valueOf();
      const fromTime = moment(from).startOf("day").valueOf();
      const toTime = moment(to).startOf("day").valueOf();

      return (
        dayTime > Math.min(fromTime, toTime) &&
        dayTime < Math.max(fromTime, toTime)
      );
    },
  };

  const isDayDisabled = (day, disabledDays) => {
    return !disabledDays.some((disabledDay) =>
      DateUtils.isSameDay(day, disabledDay)
    );
  };

  const firstAvailableDate =
    props.startingDate && moment(props.startingDate).isValid()
      ? urlDateToRawDate(props.startingDate)
      : undefined;
  const fromMonthDate =
    props.fromMonth && moment(props.fromMonth).isValid()
      ? urlDateToRawDate(props.fromMonth)
      : undefined;
  const toMonthDate =
    props.toMonth && moment(props.toMonth).isValid()
      ? urlDateToRawDate(props.toMonth)
      : undefined;
  const firstDayOfWeek = countriesWithUSFormat.includes(
    defaultCountry.toLowerCase()
  )
    ? 0
    : 1;
  const numberOfMonths = isDesktop
    ? props.numberOfMonths ?? 2
    : props.mobileNumberOfMonths ?? props.numberOfMonths ?? 2;

  useEffect(() => {
    const calendarElement = calendarContainerRef.current;

    if (!calendarElement) {
      return;
    }

    const dayElements = calendarElement.querySelectorAll<HTMLElement>(
      ".DayPicker-Day"
    );
    const handleDayKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      const dayElement = event.currentTarget as HTMLElement;
      const isDisabled =
        dayElement.classList.contains("DayPicker-Day--disabled") ||
        dayElement.classList.contains("DayPicker-Day--outside");

      if (isDisabled) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      dayElement.dispatchEvent(
        new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          view: window,
        })
      );
    };

    dayElements.forEach((dayElement) => {
      const isFocusable =
        dayElement.classList.contains("DayPicker-Day--keyboardFocusable") &&
        !dayElement.classList.contains("DayPicker-Day--disabled") &&
        !dayElement.classList.contains("DayPicker-Day--outside");

      dayElement.setAttribute("tabindex", isFocusable ? "0" : "-1");
      dayElement.addEventListener("keydown", handleDayKeyDown, true);
    });

    return () => {
      dayElements.forEach((dayElement) => {
        dayElement.removeEventListener("keydown", handleDayKeyDown, true);
      });
    };
  }, [
    props.availableDates,
    props.focusableDates,
    props.highlightedDates,
    props.rangeDates,
    props.selectedDate,
    props.selectedDateLabels,
    props.startingDate,
    numberOfMonths,
  ]);

  if (!firstAvailableDate) {
    return null;
  }

  const getNavbarElement = (e) => {
    const isPreviousDisabled = e.showPreviousButton === false;
    const isNextDisabled = e.showNextButton === false;

    return (
      <div className="DayPicker-NavBar">
        <button
          id={`${props.id}-previous-btn`}
          onClick={() => {
            if (!isPreviousDisabled && e.onPreviousClick) {
              e.onPreviousClick();
            }
          }}
          type="button"
          aria-disabled={isPreviousDisabled}
          aria-label="Previous Month"
          className={`DayPicker-NavButton DayPicker-NavButton--prev${
            isPreviousDisabled ? " DayPicker-NavButton--disabled" : ""
          }`}
          disabled={isPreviousDisabled}
        />
        <button
          id={`${props.id}-next-btn`}
          onClick={() => {
            if (!isNextDisabled && e.onNextClick) {
              e.onNextClick();
            }
          }}
          type="button"
          aria-disabled={isNextDisabled}
          aria-label="Next Month"
          className={`DayPicker-NavButton DayPicker-NavButton--next${
            isNextDisabled ? " DayPicker-NavButton--disabled" : ""
          }`}
          disabled={isNextDisabled}
        />
      </div>
    );
  };

  const getCaptionElement = (e) => (
    <div className="DayPicker-Caption header-search-form-calendar-month-title-text">
      {props.locale === "al"
        ? moment(e.date).format("MMMM YYYY")
        : monthYear(e.date, props.locale)}
    </div>
  );

  const getSelectedDateLabelClassName = (
    label?: string,
    variant?: "roundTrip"
  ): string => {
    if (variant === "roundTrip") {
      return "best-offer-calendar-day__label best-offer-calendar-day__label--round-trip";
    }

    const labelLength = label?.trim().length || 0;

    if (labelLength > 9) {
      return "best-offer-calendar-day__label best-offer-calendar-day__label--narrow";
    }

    if (labelLength > 7) {
      return "best-offer-calendar-day__label best-offer-calendar-day__label--compact";
    }

    return "best-offer-calendar-day__label";
  };

  const getSelectedDateLabelStyle = (
    label?: string
  ): React.CSSProperties | undefined => {
    const labelLength = label?.trim().length || 0;

    if (labelLength <= 6) {
      return undefined;
    }

    const shrinkFactor = Math.min(labelLength - 6, 4);
    const desktopFontSize = Math.max(8, 11 - shrinkFactor * 0.75);
    const mobileFontSize = Math.max(7, 9 - shrinkFactor * 0.5);

    return {
      ["--best-offer-calendar-label-font-size" as string]: `${desktopFontSize}px`,
      ["--best-offer-calendar-label-mobile-font-size" as string]: `${mobileFontSize}px`,
    };
  };

  const renderDay = (day: Date, dayModifiers: DayModifiers) => {
    if (dayModifiers.outside) {
      return null;
    }

    const dayFooter = props.renderDayFooter?.(day);
    const currentCalendarSelectedLabel: SelectedDateLabel | undefined =
      props.selectedDate &&
      props.selectedLabel &&
      DateUtils.isSameDay(day, props.selectedDate)
        ? { label: props.selectedLabel }
        : undefined;
    const selectedDateLabel =
      currentCalendarSelectedLabel ||
      selectedDateLabels.find(({ date }) => DateUtils.isSameDay(day, date));
    const labelClassName = getSelectedDateLabelClassName(
      selectedDateLabel?.label,
      selectedDateLabel?.variant
    );
    const labelStyle = selectedDateLabel?.variant
      ? undefined
      : getSelectedDateLabelStyle(selectedDateLabel?.label);

    return (
      <span
        className={`best-offer-calendar-day${
          dayFooter ? " best-offer-calendar-day--has-footer" : ""
        }`}
      >
        {selectedDateLabel && (
          <span
            aria-label={
              selectedDateLabel.variant === "roundTrip"
                ? selectedDateLabel.label
                : undefined
            }
            className={labelClassName}
            role={selectedDateLabel.variant === "roundTrip" ? "img" : undefined}
            style={labelStyle}
            title={
              selectedDateLabel.variant === "roundTrip"
                ? selectedDateLabel.label
                : undefined
            }
          >
            {selectedDateLabel.variant === "roundTrip" ? (
              <>
                <PlaneTakeoff aria-hidden="true" size={10} />
                <PlaneLanding aria-hidden="true" size={10} />
              </>
            ) : (
              selectedDateLabel.label
            )}
          </span>
        )}
        <span className="best-offer-calendar-day__date">{day.getDate()}</span>
        {dayFooter && (
          <span className="best-offer-calendar-day__footer">{dayFooter}</span>
        )}
      </span>
    );
  };

  const handleDayClick = (day: Date, dayModifiers: DayModifiers) => {
    if (!dayModifiers.outside) {
      props.onDayClick(day, dayModifiers);
    }
  };

  const handleDayMouseEnter = (day: Date, dayModifiers: DayModifiers) => {
    if (!dayModifiers.outside) {
      props.onDayMouseEnter?.(day);
    }
  };

  return (
    <div
      className={[
        "trip-date-calendar",
        props.showHighlightedDateBorders !== false &&
          "trip-date-calendar--highlighted-date-borders",
        props.className,
      ]
        .filter(Boolean)
        .join(" ")}
      ref={calendarContainerRef}
    >
      <Calendar
        numberOfMonths={numberOfMonths}
        fixedWeeks={props.fixedWeeks}
        firstDayOfWeek={firstDayOfWeek}
        month={firstAvailableDate}
        fromMonth={fromMonthDate}
        toMonth={toMonthDate}
        localeUtils={MomentLocaleUtils}
        locale={props.locale}
        enableOutsideDaysClick={false}
        modifiers={modifiers}
        disabledDays={(day) => isDayDisabled(day, props.availableDates)}
        selectedDays={selectedDays}
        onDayClick={handleDayClick}
        onDayMouseEnter={
          props.onDayMouseEnter ? handleDayMouseEnter : undefined
        }
        onMonthChange={(month) => props.onCalendarChange(month)}
        navbarElement={(e) => getNavbarElement(e)}
        captionElement={(e) => getCaptionElement(e)}
        renderDay={renderDay}
      />
    </div>
  );
}

export default TripDateCalendar;
